import {useEffect, useState} from 'react'
import {ActionList, ActionMenu, Box, Label, Truncate} from '@primer/react'
import {PencilIcon, PlusIcon, ShareIcon, TrashIcon} from '@primer/octicons-react'
import {useSafeAsyncCallback} from '@github-ui/use-safe-async-callback'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import {PresetDialog} from './PresetDialog'
import {DeletePresetDialog} from './DeletePresetDialog'
import {SharePresetDialog} from './SharePresetDialog'
import type {Model, Preset, PresetsPayload} from '../../../../../types'
import {usePlaygroundManager} from '../../../../utils/playground-manager'
import {ModelUrlHelper} from '../../../../utils/model-url-helper'
import {createPreset, deletePreset, updatePreset} from '../../../../utils/presets'
import {useLocation} from 'react-router-dom'

type PresetsMenuProps = {
  model: Model
}

export function PresetsMenu({model}: PresetsMenuProps) {
  const manager = usePlaygroundManager()
  const defaultPreset = manager.getDefaultPreset()

  const [presets, setPresets] = useState<Preset[]>([defaultPreset])
  const [limitPerUser, setLimitPerUser] = useState(0)

  const location = useLocation()
  const urlSearchParams = new URLSearchParams(location.search)
  const presetFromURL: string = urlSearchParams.get('preset')?.toLowerCase() ?? ''

  const [selectedIndex, setSelectedIndex] = useState(0)
  const selectedPreset = presets[selectedIndex] ?? null

  const [showDeletePresetDialog, setShowDeletePresetDialog] = useState(false)
  const [showPresetDialog, setShowPresetDialog] = useState(false)
  const [showSharePresetDialog, setShowSharePresetDialog] = useState(false)
  const [showDeletePresetDialogErrors, setShowDeletePresetDialogErrors] = useState<string>('')
  const [showPresetDialogErrors, setShowPresetDialogErrors] = useState<object | null>(null)
  const [action, setAction] = useState<'create' | 'update'>('create')

  const loadPresets = useSafeAsyncCallback(async () => {
    const res = await verifiedFetchJSON('/marketplace/models/presets')
    if (res?.ok) {
      const presetsResponse: PresetsPayload = await res.json()
      const updatedPresets = [defaultPreset, ...presetsResponse.presets]
      setPresets(updatedPresets)
      setLimitPerUser(presetsResponse.limit_per_user)

      // If a preset is specified in the URL, find it and set it as the selected index
      if (presetFromURL) {
        const presetIndex = updatedPresets.findIndex(preset => preset.urlIdentifier.toLowerCase() === presetFromURL)
        if (presetIndex > 0) {
          setSelectedIndex(presetIndex)
        } else {
          setSelectedIndex(0)
        }
      }
    }
  })

  useEffect(() => {
    loadPresets()

    if (!showDeletePresetDialog) {
      setShowDeletePresetDialogErrors('')
    }
  }, [loadPresets, showDeletePresetDialog])

  const onSavePreset = async (presetData: Preset) => {
    try {
      const {urlIdentifier, includeChatHistory, ...savePresetData} = presetData

      // Add logic to check if includeChatHistory is defined and false
      if (includeChatHistory === false) {
        savePresetData.conversationHistory = []
      }

      const isCreating = action === 'create'
      const res = isCreating
        ? await createPreset({preset: savePresetData})
        : await updatePreset({urlIdentifier, preset: savePresetData})

      if (res.ok) {
        setShowPresetDialogErrors({})
        const newPreset = (await res.json()) as Preset

        if (isCreating) {
          setPresets([...presets, newPreset])
          setSelectedIndex(presets.length)
        } else {
          const updatedPresets = presets.map(preset => (preset.urlIdentifier === urlIdentifier ? newPreset : preset))
          setPresets(updatedPresets)
        }
      } else {
        const body = await res.json()
        setShowPresetDialogErrors(body.error)
        return
      }
    } catch {
      setShowPresetDialogErrors({error: [`Failed to ${action} preset: ${presetData.name}`]})
      return
    }
    setShowPresetDialog(false)
  }

  const onDeletePreset = async (presetData: Preset) => {
    try {
      const {urlIdentifier} = presetData
      const res = await deletePreset({urlIdentifier})

      if (res.ok) {
        const updatedPresets = presets.filter(preset => preset.urlIdentifier !== urlIdentifier)

        setPresets(updatedPresets)
        setSelectedIndex(0) // We want it to go to the Default preset
      }
    } catch {
      setShowDeletePresetDialogErrors(`Failed to delete ${presetData.name} preset. Try again later.`)
      return
    }
    setShowDeletePresetDialog(false)
  }

  const defaultPresetSelected = selectedPreset?.name === 'Default'
  // The default preset isn't counted in the preset limit
  const limitPerUserReached = presets.length > limitPerUser
  const presetIsEditable = !defaultPresetSelected
  const showSharePreset = !defaultPresetSelected && !selectedPreset?.private

  return (
    <>
      <ActionMenu>
        <ActionMenu.Button>
          <Box
            sx={{
              color: 'fg.muted',
              display: 'inline-block',
            }}
          >
            Preset:
          </Box>{' '}
          <Truncate inline sx={{maxWidth: '40ch'}} title={`${selectedPreset?.name ?? ''}`}>
            {selectedPreset?.name ?? ''}
          </Truncate>
        </ActionMenu.Button>
        <ActionMenu.Overlay width="medium">
          <ActionList sx={{pb: '0'}}>
            <ActionList.Group>
              <ActionList.Item
                onSelect={() => {
                  setShowPresetDialog(true)
                  setShowPresetDialogErrors(null)
                  setAction('create')
                }}
                disabled={limitPerUserReached}
              >
                <ActionList.LeadingVisual>
                  <PlusIcon />
                </ActionList.LeadingVisual>
                Create new preset
              </ActionList.Item>
              {presetIsEditable ? (
                <ActionList.Item
                  onSelect={() => {
                    setShowPresetDialog(true)
                    setAction('update')
                  }}
                >
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  Edit preset
                </ActionList.Item>
              ) : null}
              {presetIsEditable ? (
                <ActionList.Item
                  onSelect={() => {
                    setShowDeletePresetDialog(true)
                  }}
                >
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  Delete preset
                </ActionList.Item>
              ) : null}
              {showSharePreset ? (
                <ActionList.Item
                  onSelect={() => {
                    setShowSharePresetDialog(true)
                  }}
                >
                  <ActionList.LeadingVisual>
                    <ShareIcon />
                  </ActionList.LeadingVisual>
                  Share preset
                </ActionList.Item>
              ) : null}
            </ActionList.Group>
            <ActionList.Group selectionVariant="single" sx={{maxHeight: '50vh', overflowY: 'scroll', pb: '2'}}>
              <ActionList.Divider sx={{mt: '0'}} />
              {presets.map((preset, index) => (
                <ActionList.Item
                  key={preset.name}
                  selected={index === selectedIndex}
                  onSelect={() => {
                    setSelectedIndex(index)
                    manager.applyPreset(preset)
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{maxWidth: '40ch'}}>{preset.name}</Box>
                    {!preset.private && <Label>Shared</Label>}
                  </Box>
                  <ActionList.Description variant="block">
                    <span className="line-clamp-2">{preset.description}</span>
                  </ActionList.Description>
                </ActionList.Item>
              ))}
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {showPresetDialog && (
        <PresetDialog
          onClose={() => setShowPresetDialog(false)}
          onSubmit={onSavePreset}
          selectedPreset={selectedPreset}
          action={action}
          errors={showPresetDialogErrors}
        />
      )}
      {showDeletePresetDialog && selectedPreset && (
        <DeletePresetDialog
          onClose={() => setShowDeletePresetDialog(false)}
          onSubmit={onDeletePreset}
          selectedPreset={selectedPreset}
          errors={showDeletePresetDialogErrors}
        />
      )}
      {showSharePresetDialog && (
        <SharePresetDialog
          onClose={() => setShowSharePresetDialog(false)}
          playgroundUrl={ModelUrlHelper.playgroundUrl(model)}
          urlIdentifier={selectedPreset?.urlIdentifier}
        />
      )}
    </>
  )
}

try{ PresetsMenu.displayName ||= 'PresetsMenu' } catch {}